/** Einstellungen › Abrechnung (Screen Einst-Abrechnung, MVP): Periode, Mindestbetrag, Auszahlungswege, Bankverbindung, USt-Hinweis, Exporte. DATEV/lexoffice sind Später. */
import { useEffect, useState, type FormEvent } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router";
import { Button, Notice, Seitenzustand, TextField } from "../ui";
import { Card, FormGrid, PageHeader, Select } from "../ui/Layout";
import { api, ApiFehler, rufe } from "../api/client";
import { parseEuro } from "@shared/geld";
import { tagVon } from "@shared/zeit";
import type { LadenEinstellungen } from "@shared/schemas/laden";
import { useLaden, type LadenDaten } from "./api";
import { exportUrl, useBank, useBankSpeichern } from "./geld-api";

export function EinstellungenAbrechnung() {
  const laden = useLaden();
  return (
    <>
      <PageHeader kicker="Einstellungen" title="Abrechnung" sub="Periode, Mindestbetrag, Auszahlungswege, Bankverbindung und Exporte" />
      <Seitenzustand query={laden}>{(d) => <AbrechnungFormular daten={d} />}</Seitenzustand>
      <BankKarte />
      <ExportKarte />
    </>
  );
}

function AbrechnungFormular({ daten }: { daten: LadenDaten }) {
  const qc = useQueryClient();
  const e = daten.einstellungen;
  const [form, setForm] = useState({ periode: e.abrechnung.periode, mindest: (e.abrechnung.mindestbetragCent / 100).toFixed(2).replace(".", ","), ueberweisung: e.abrechnung.auszahlungswege.includes("ueberweisung"), bar: e.abrechnung.auszahlungswege.includes("bar") , glaeubigerId: e.sepa.glaeubigerId, vorlaufTage: e.sepa.vorlaufTage, mw: { ...e.mahnwesen, gebuehr1: (e.mahnwesen.gebuehr1Cent / 100).toFixed(2).replace(".", ","), gebuehr2: (e.mahnwesen.gebuehr2Cent / 100).toFixed(2).replace(".", ",") } });
  const speichern = useMutation({ mutationFn: (neu: LadenEinstellungen) => rufe(api.api.laden.einstellungen.$put({ json: neu })), onSuccess: () => void qc.invalidateQueries({ queryKey: ["laden"] }) });
  const [erfolg, setErfolg] = useState(false);
  useEffect(() => {
    if (!erfolg) return;
    const t = setTimeout(() => setErfolg(false), 4000);
    return () => clearTimeout(t);
  }, [erfolg]);
  function absenden(ev: FormEvent) {
    ev.preventDefault();
    const wege: ("ueberweisung" | "bar")[] = [];
    if (form.ueberweisung) wege.push("ueberweisung");
    if (form.bar) wege.push("bar");
    speichern.mutate({ ...e, abrechnung: { periode: form.periode, mindestbetragCent: parseEuro(form.mindest) ?? 1000, auszahlungswege: wege.length ? wege : ["ueberweisung"] }, sepa: { glaeubigerId: form.glaeubigerId.trim().toUpperCase(), vorlaufTage: form.vorlaufTage }, mahnwesen: { aktiv: form.mw.aktiv, erinnerungTage: form.mw.erinnerungTage, mahnung1Tage: form.mw.mahnung1Tage, mahnung2Tage: form.mw.mahnung2Tage, gebuehr1Cent: parseEuro(form.mw.gebuehr1) ?? 0, gebuehr2Cent: parseEuro(form.mw.gebuehr2) ?? 0, sperreNachStufe3: form.mw.sperreNachStufe3 } }, { onSuccess: () => setErfolg(true) });
  }
  return (
    <form className="app-form" onSubmit={absenden} noValidate>
      {erfolg ? <Notice tone="success" title="Gespeichert">Gilt für die nächste Abrechnung.</Notice> : null}
      {speichern.isError ? <Notice tone="error">{speichern.error instanceof ApiFehler ? speichern.error.message : "Speichern hat nicht geklappt."}</Notice> : null}
      <Card title="Abrechnungsperiode">
        <div className="sc-toolbar" role="radiogroup" aria-label="Periode">
          <button type="button" className="sc-pill" aria-pressed={form.periode === "woche"} onClick={() => setForm({ ...form, periode: "woche" })}>Wöchentlich (Mo–So)</button>
          <button type="button" className="sc-pill" aria-pressed={form.periode === "monat"} onClick={() => setForm({ ...form, periode: "monat" })}>Monatlich</button>
        </div>
        <FormGrid>
          <TextField label="Mindestbetrag für Auszahlung" value={form.mindest} onChange={(ev) => setForm({ ...form, mindest: ev.target.value })} inputMode="decimal" suffix="€" hint="Kleinere Guthaben gehen in die nächste Periode" />
        </FormGrid>
        <p className="sc-muted sc-small">Abrechnungen erstellt das Personal nach Periodenende unter Geld › Abrechnungen (Vorschau jederzeit). Stichtag ist das Periodenende.</p>
      </Card>
      <Card title="Auszahlungswege">
        <p className="sc-muted">Mieter wählen im Portal aus den freigegebenen Wegen.</p>
        <label className="sc-check"><input type="checkbox" checked={form.ueberweisung} onChange={(ev) => setForm({ ...form, ueberweisung: ev.target.checked })} /> Überweisung (manuell, „als überwiesen markieren“; SEPA-Sammeldatei in V2)</label>
        <label className="sc-check"><input type="checkbox" checked={form.bar} onChange={(ev) => setForm({ ...form, bar: ev.target.checked })} /> Bar im Laden abholen (mit Quittung und Unterschrift)</label>
      </Card>
      <Card title="SEPA-Lastschrift (E9)">
        <p className="sc-muted">Mit Gläubiger-ID können Mieter im Portal ein Lastschriftmandat erteilen; offene Miete ziehst du dann unter <Link to="/laden/sepa">Geld › SEPA</Link> als Datei ein. Die Gläubiger-ID gibt die Bundesbank aus.</p>
        <FormGrid>
          <TextField label="Gläubiger-Identifikationsnummer" value={form.glaeubigerId} onChange={(ev) => setForm({ ...form, glaeubigerId: ev.target.value })} placeholder="DE98ZZZ09999999999" autoComplete="off" />
          <TextField label="Vorlauf bis Einzug (Tage)" type="number" inputMode="numeric" min={2} max={14} value={form.vorlaufTage} onChange={(ev) => setForm({ ...form, vorlaufTage: Math.max(2, Math.min(14, Number(ev.target.value) || 6)) })} hint="Vorabankündigung geht automatisch per E-Mail" />
        </FormGrid>
      </Card>
      <Card title="Mahnwesen (E9)">
        <label className="sc-check"><input type="checkbox" checked={form.mw.aktiv} onChange={(ev) => setForm({ ...form, mw: { ...form.mw, aktiv: ev.target.checked } })} /> Täglicher Mahnlauf für offene Mietrechnungen</label>
        <FormGrid>
          <TextField label="Erinnerung nach Tagen" type="number" inputMode="numeric" min={1} max={60} value={form.mw.erinnerungTage} onChange={(ev) => setForm({ ...form, mw: { ...form.mw, erinnerungTage: Number(ev.target.value) || 7 } })} />
          <TextField label="Mahnung nach Tagen" type="number" inputMode="numeric" min={2} max={90} value={form.mw.mahnung1Tage} onChange={(ev) => setForm({ ...form, mw: { ...form.mw, mahnung1Tage: Number(ev.target.value) || 14 } })} />
          <TextField label="Letzte Mahnung nach Tagen" type="number" inputMode="numeric" min={3} max={120} value={form.mw.mahnung2Tage} onChange={(ev) => setForm({ ...form, mw: { ...form.mw, mahnung2Tage: Number(ev.target.value) || 28 } })} />
          <TextField label="Gebühr Mahnung" value={form.mw.gebuehr1} onChange={(ev) => setForm({ ...form, mw: { ...form.mw, gebuehr1: ev.target.value } })} inputMode="decimal" suffix="€" />
          <TextField label="Gebühr letzte Mahnung" value={form.mw.gebuehr2} onChange={(ev) => setForm({ ...form, mw: { ...form.mw, gebuehr2: ev.target.value } })} inputMode="decimal" suffix="€" />
        </FormGrid>
        <label className="sc-check"><input type="checkbox" checked={form.mw.sperreNachStufe3} onChange={(ev) => setForm({ ...form, mw: { ...form.mw, sperreNachStufe3: ev.target.checked } })} /> Bei letzter Mahnung Auszahlung einbehalten (Guthaben wird verrechnet, Verkäufe laufen weiter)</label>
        <p className="sc-muted sc-small">Mahngebühren sind pauschaler Verzugsschaden ohne Umsatzsteuer und müssen angemessen sein (üblich 2,50 € bis 5 €). Alle Mahnungen stehen unter Forderungen, Storno als Gegenrechnung.</p>
      </Card>
      <Card title="Umsatzsteuer">
        <p className="sc-muted">Der USt-Modus steht unter <Link to="/laden/einstellungen">Einstellungen › Laden</Link>: zurzeit {daten.ustModus === "kleinunternehmer" ? "Kleinunternehmer (ohne USt, § 19 UStG)" : "Regelbesteuerung, 19 % auf Miete und Provision"}. Mietrechnungen weisen Netto, USt und Brutto aus; Abrechnungen zeigen Bruttobeträge mit Hinweis. Gewerbliche Mieter werden wie private dargestellt (Frage 9). Die Software ersetzt keine Steuerberatung.</p>
      </Card>
      <div className="app-form__actions">
        <Button type="submit" variant="primary" icon="check" loading={speichern.isPending}>Speichern</Button>
      </div>
    </form>
  );
}

function BankKarte() {
  const bank = useBank();
  const speichern = useBankSpeichern();
  const [form, setForm] = useState<{ inhaber: string; iban: string; bic: string; bank: string } | null>(null);
  const [erfolg, setErfolg] = useState(false);
  return (
    <Seitenzustand query={bank} lines={2}>
      {(d) => {
        const f = form ?? { inhaber: d.bank?.inhaber ?? "", iban: d.bank?.iban ?? "", bic: d.bank?.bic ?? "", bank: d.bank?.bank ?? "" };
        return (
          <Card title="Bankverbindung des Ladens" className="app-form">
            <p className="sc-muted">Steht auf Mietrechnungen, falls ein Mieter direkt überweisen muss. Wird verschlüsselt gespeichert.</p>
            {erfolg ? <Notice tone="success">Gespeichert.</Notice> : null}
            <FormGrid>
              <TextField label="Kontoinhaber" value={f.inhaber} onChange={(e) => setForm({ ...f, inhaber: e.target.value })} />
              <TextField label="IBAN" value={f.iban} onChange={(e) => setForm({ ...f, iban: e.target.value })} autoComplete="off" />
              <TextField label="BIC" value={f.bic} onChange={(e) => setForm({ ...f, bic: e.target.value })} />
              <TextField label="Bank" value={f.bank} onChange={(e) => setForm({ ...f, bank: e.target.value })} />
            </FormGrid>
            <div className="app-form__actions">
              <Button variant="primary" icon="check" loading={speichern.isPending} disabled={!form} onClick={() => speichern.mutate(f, { onSuccess: () => { setForm(null); setErfolg(true); } })}>Bankverbindung speichern</Button>
            </div>
          </Card>
        );
      }}
    </Seitenzustand>
  );
}

function ExportKarte() {
  const heute = tagVon();
  const [von, setVon] = useState(`${heute.slice(0, 7)}-01`);
  const [bis, setBis] = useState(heute);
  const [art, setArt] = useState("verkaeufe");
  return (
    <Card title="Exporte (CSV für Excel und Steuerbüro)" className="app-form">
      <FormGrid>
        <Select label="Daten" value={art} onChange={(e) => setArt(e.target.value)}>
          <option value="verkaeufe">Verkäufe (je Position)</option>
          <option value="abrechnungen">Abrechnungen</option>
          <option value="auszahlungen">Auszahlungen</option>
          <option value="mietrechnungen">Mietrechnungen</option>
          <option value="mieterkonto">Mieterkonto (Journal)</option>
          <option value="kassenbuch">Kassenbuch</option>
          <option value="protokoll">Änderungsprotokoll</option>
        </Select>
        <TextField label="Von" type="date" value={von} onChange={(e) => setVon(e.target.value)} />
        <TextField label="Bis" type="date" value={bis} onChange={(e) => setBis(e.target.value)} />
      </FormGrid>
      <div className="app-form__actions">
        <a href={exportUrl(art, von, bis)} className="rg-btn rg-btn--secondary">CSV herunterladen</a>
        <a href={`/api/laden/export-alles.zip?von=${von}&bis=${bis}`} className="rg-btn rg-btn--secondary">Alles als ZIP</a>
        <span className="app-help">Semikolon-getrennt, UTF-8 mit BOM, Beträge mit Komma. DATEV/lexoffice kommen später.</span>
      </div>
    </Card>
  );
}
